import { Button } from '@statsbomb/kitbag-components';

import {
  CompetitionSelector,
  FilterPanel,
  FilterScopeProvider,
  SeasonSelector,
  SingleGameSelector,
  TeamSelectorWithAllOption,
  useFilters,
} from '@statsbomb/kitbag-global-football';
import { useAtom, useAtomValue } from 'jotai';
import { unwrap } from 'jotai/utils';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { competitionOptionsAtom, defaultCompetitionSectionCycleAtom } from '@/atoms/filters/highLevel/competitions';
import { seasonOptionsAtom } from '@/atoms/filters/highLevel/seasons';
import { teamOptionsAtom } from '@/atoms/filters/highLevel/teams';
import { gamesFilteredByTeamsAndDateAtom, selectedGameIdAtom } from '@/atoms/queries/game';
import { usePhysicalMetricsFeature } from '@/hooks/featureFlags';
import { getFormattedGameName } from '@/utils/games';
import { LayoutWithFilters } from '../LayoutWithFilters';

const unwrappedCompetitionOptionsAtom = unwrap(competitionOptionsAtom, prev => prev ?? []);
const unwrappedSeasonOptionsAtom = unwrap(seasonOptionsAtom, prev => prev ?? []);
const unwrappedTeamOptionsAtom = unwrap(teamOptionsAtom, prev => prev ?? []);
const unwrappedCyclesAtom = unwrap(defaultCompetitionSectionCycleAtom, prev => prev ?? []);
const unwrappedGamesAtom = unwrap(gamesFilteredByTeamsAndDateAtom, prev => prev ?? []);

const GameLayoutFilters = () => {
  const { t } = useTranslation('filters');
  const competitionOptions = useAtomValue(unwrappedCompetitionOptionsAtom);
  const seasonOptions = useAtomValue(unwrappedSeasonOptionsAtom);
  const teamOptions = useAtomValue(unwrappedTeamOptionsAtom);
  const cycles = useAtomValue(unwrappedCyclesAtom);
  const games = useAtomValue(unwrappedGamesAtom);
  const [{ teamIds: selectedTeamIds }] = useFilters('main');
  const [selectedGameId, setSelectedGameId] = useAtom(selectedGameIdAtom);
  const showTracking = usePhysicalMetricsFeature();

  const gameOptions = games.map(game => ({ value: game.game_id, label: getFormattedGameName(game) }));
  const allTeamsSelected = teamOptions.length > 0 && selectedTeamIds.length === teamOptions.length;

  return (
    <FilterPanel>
      <CompetitionSelector
        options={competitionOptions}
        playerCycles={cycles}
        isMulti={false}
        showTracking={showTracking}
      />
      <SeasonSelector options={seasonOptions} isMulti={false} />
      <TeamSelectorWithAllOption options={teamOptions} />
      <SingleGameSelector
        options={gameOptions}
        selectedGameId={selectedGameId}
        onSelect={setSelectedGameId}
        allTeamsSelected={allTeamsSelected}
      />
      <div>
        <Button variant="primary" size="small" disabled title={t('notYetImplemented')}>
          {t('selectMatch')}
        </Button>
      </div>
    </FilterPanel>
  );
};

export const GameLayoutWithFilters = ({ children }: { children: ReactNode }) => (
  <FilterScopeProvider scope="main">
    <LayoutWithFilters filters={<GameLayoutFilters />}>{children}</LayoutWithFilters>
  </FilterScopeProvider>
);
